import React, { useState } from 'react';
import { FileText, Image as ImageIcon, Eye, Download, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatBytes } from './poDocumentRules';

const when = (iso) => (iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

export const PODocumentRow = ({ doc, canRemove, canOpen = true, onView, onDownload, onRemove }) => {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const Icon = doc.content_type === 'application/pdf' ? FileText : ImageIcon;
  const removed = !!doc.removed_at;

  const remove = async () => {
    setRemoving(true);
    try { await onRemove(doc); } finally { setRemoving(false); setConfirming(false); }
  };

  return (
    <div className={`flex items-center gap-3 rounded-md border px-3 py-2 ${removed ? 'bg-muted/40' : 'bg-background'}`} data-testid={`po-doc-row-${doc.id}`}>
      <Icon className={`h-5 w-5 shrink-0 ${removed ? 'text-foreground/70' : 'text-muted-foreground'}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`truncate text-sm font-medium ${removed ? 'line-through' : ''}`} data-testid={`po-doc-name-${doc.id}`}>{doc.file_name}</span>
          <Badge variant="secondary" className="text-[10px]" data-testid={`po-doc-label-${doc.id}`}>{doc.label_display}</Badge>
        </div>
        <p className="text-xs text-muted-foreground break-words">
          {removed
            ? `Removed by ${doc.removed_by_name || 'Unknown'}, ${when(doc.removed_at)} · added by ${doc.uploaded_by_name || 'Unknown'}`
            : `${formatBytes(doc.file_size)} · ${doc.uploaded_by_name || 'Unknown'}, ${when(doc.created_at)}`}
        </p>
      </div>
      {confirming ? (
        <div className="flex items-center gap-1" data-testid={`po-doc-confirm-${doc.id}`}>
          <span className="text-xs text-muted-foreground">Remove?</span>
          <Button type="button" size="sm" variant="destructive" className="h-7 px-2" disabled={removing} onClick={remove} data-testid={`po-doc-confirm-yes-${doc.id}`}>
            {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Yes'}
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => setConfirming(false)} data-testid={`po-doc-confirm-no-${doc.id}`}>No</Button>
        </div>
      ) : (
        <div className="flex items-center gap-0.5">
          {canOpen && (
            <>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onView(doc)} title="View" data-testid={`po-doc-view-${doc.id}`}><Eye className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onDownload(doc)} title="Download" data-testid={`po-doc-download-${doc.id}`}><Download className="h-4 w-4" /></Button>
            </>
          )}
          {canRemove && !removed && (
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setConfirming(true)} title="Remove" data-testid={`po-doc-remove-${doc.id}`}><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      )}
    </div>
  );
};
